import { Link } from 'react-router-dom';
import { Store } from '../../config';
import { GROUP_PROFILES, companies } from '../../seo/organizations';

/**
 * The footer column that ties a store to the Ondio brand: the group, its other markets and its
 * profiles, on all five markets. In Sweden the store still trades as Kontorsgiganten and the link to
 * Ondio is the thing to strengthen; elsewhere the column points to the group's other markets. The
 * copy is kept here rather than in the translations because those are typed off the Swedish file,
 * and a market's column is approved copy of its own rather than a translation of another's.
 */

export type OndioColumn = { heading: string; items: JSX.Element[] };

const external = (href: string, text: string) => (
	<a href={href} target="_blank" rel="noopener">
		{text}
	</a>
);

const subheading = (text: string) => <span className="subheading">{text}</span>;

// flags as images rather than emoji, which windows draws as two plain letters; the files are
// twemoji's own flag emoji (jdecked/twemoji 17.0.3, CC-BY 4.0), so they look like the emoji
// everywhere. The alt is empty because the link text already names the country.
const market = (code: string, url: string, text: string) => (
	<a href={url} target="_blank" rel="noopener">
		<img
			className="market-flag"
			src={`/images/flags/flag-${code}.svg`}
			alt=""
			width="18"
			height="18"
			loading="lazy"
		/>{' '}
		{text}
	</a>
);

// the brand's site for customers; the group's own site is the group company's url
const ONDIO_COM = 'https://ondio.com/';

const columns: Partial<Record<Store, () => OndioColumn>> = {
	kgse: () => ({
		heading: 'Ondio',
		items: [
			<Link to="/ondio">Kontorsgiganten – en del av Ondio</Link>,
			external(ONDIO_COM, 'Ondio.com'),
			external(companies.ondioGroup.url, 'Ondio Group'),
			subheading('Ondio i Europa'),
			market('no', companies.ondioNo.url, 'Norge – Ondio'),
			market('dk', companies.ondioDk.url, 'Danmark – Ondio'),
			market('nl', companies.ondioNl.url, 'Nederländerna – Ondio'),
			market('de', companies.ondioDe.url, 'Tyskland – Ondio'),
			subheading('Följ Ondio'),
			external(GROUP_PROFILES.linkedIn, 'LinkedIn'),
			external(GROUP_PROFILES.youTube, 'YouTube'),
		],
	}),
	kgno: () => ({
		heading: 'Ondio',
		items: [
			external(ONDIO_COM, 'Ondio.com'),
			external(companies.ondioGroup.url, 'Ondio Group'),
			subheading('Ondio i Europa'),
			market('se', companies.kontorsgiganten.url, 'Sverige – Kontorsgiganten'),
			market('dk', companies.ondioDk.url, 'Danmark – Ondio'),
			market('nl', companies.ondioNl.url, 'Nederland – Ondio'),
			market('de', companies.ondioDe.url, 'Tyskland – Ondio'),
			subheading('Følg Ondio'),
			external(GROUP_PROFILES.linkedIn, 'LinkedIn'),
			external(GROUP_PROFILES.youTube, 'YouTube'),
		],
	}),
	kgdk: () => ({
		heading: 'Ondio',
		items: [
			external(ONDIO_COM, 'Ondio.com'),
			external(companies.ondioGroup.url, 'Ondio Group'),
			subheading('Ondio i Europa'),
			market('se', companies.kontorsgiganten.url, 'Sverige – Kontorsgiganten'),
			market('no', companies.ondioNo.url, 'Norge – Ondio'),
			market('nl', companies.ondioNl.url, 'Nederlandene – Ondio'),
			market('de', companies.ondioDe.url, 'Tyskland – Ondio'),
			subheading('Følg Ondio'),
			external(GROUP_PROFILES.linkedIn, 'LinkedIn'),
			external(GROUP_PROFILES.youTube, 'YouTube'),
		],
	}),
	kgnl: () => ({
		heading: 'Ondio',
		items: [
			external(ONDIO_COM, 'Ondio.com'),
			external(companies.ondioGroup.url, 'Ondio Group'),
			subheading('Ondio in Europa'),
			market('se', companies.kontorsgiganten.url, 'Zweden – Kontorsgiganten'),
			market('no', companies.ondioNo.url, 'Noorwegen – Ondio'),
			market('dk', companies.ondioDk.url, 'Denemarken – Ondio'),
			market('de', companies.ondioDe.url, 'Duitsland – Ondio'),
			subheading('Volg Ondio'),
			external(GROUP_PROFILES.linkedIn, 'LinkedIn'),
			external(GROUP_PROFILES.youTube, 'YouTube'),
		],
	}),
	kgde: () => ({
		heading: 'Ondio',
		items: [
			external(ONDIO_COM, 'Ondio.com'),
			external(companies.ondioGroup.url, 'Ondio Group'),
			subheading('Ondio in Europa'),
			market('se', companies.kontorsgiganten.url, 'Schweden – Kontorsgiganten'),
			market('no', companies.ondioNo.url, 'Norwegen – Ondio'),
			market('dk', companies.ondioDk.url, 'Dänemark – Ondio'),
			market('nl', companies.ondioNl.url, 'Niederlande – Ondio'),
			subheading('Folgen Sie Ondio'),
			external(GROUP_PROFILES.linkedIn, 'LinkedIn'),
			external(GROUP_PROFILES.youTube, 'YouTube'),
		],
	}),
};

/**
 * The logo of the site each column is written for. A store is shared by more than one config - the
 * Ondio Sweden configs use `kgse`, the old kontorgiganten.no config `kgno`, and the Worky configs
 * `kgdk`, whose store name even says Ondio - so the logo the footer shows is what tells the brands
 * apart, and a column belongs only beside the logo it speaks for.
 */
const columnLogo: Partial<Record<Store, string>> = {
	kgse: 'kgse-logo.svg',
	kgno: 'ondio-logo.svg',
	kgdk: 'ondio-logo.svg',
	kgnl: 'ondio-logo.svg',
	kgde: 'ondio-logo.svg',
};

export function ondioColumn(store: Store, logo: string): OndioColumn | null {
	if (columnLogo[store] !== logo) return null;
	return columns[store]?.() ?? null;
}
